// Cash and ledgers (docs/cockpit/spec.md, under Money and compliance; #486): the money in the bank and in hand, what
// customers owe and what the business owes its suppliers with how late each is, the 13-week cash forecast, and each
// Ledger account with its newest entries, each opening its Run, or the invoice's PDF when it has none, and the stock
// Ledger's balance, what the stock on hand is worth (#1013). It reads only
// `GET /api/cash` and changes nothing: recording a payment or a bill is done in Chat. A part the person's Roles hide
// says which classes it needs; a business with nothing recorded is told what to record, never shown a page of zeros.
// The bank's balance starts from the opening balance the business's books give it (#1004); without one the page says
// since when LAMP has the bank's postings and that the opening balance is not given, never a balance as if the bank had
// started from nil, and asks a person who keeps the accounts for it.
import { useEffect, useState, type FormEvent, type MouseEvent, type ReactNode } from "react";
import { rupees } from "../api.js";
import { ApiError } from "../http.js";
import { useLanguage, useWords } from "../i18n/words.js";
import { dayOf } from "../lists/List.js";
import { PAGES } from "../shell/nav.js";
import {
  AGEING_BUCKETS,
  type Ageing,
  type BankOpening,
  type CashApi,
  type CashPage,
  type LedgerAccount,
  type Unshown,
} from "./api.js";

/** Rupees as typed, e.g. "2,50,000" or "-1500.50", in paise; undefined when it is not an amount. */
export function typedPaise(typed: string): number | undefined {
  const plain = typed.replaceAll(",", "").replace("−", "-").trim();
  if (!/^-?\d+(\.\d{1,2})?$/.test(plain)) return undefined;
  return Math.round(Number(plain) * 100);
}

/** Asks a person who keeps the accounts for the bank's opening balance; `onGiven` once it is kept. */
function GiveOpening({ api, asOf, onGiven }: { api: CashApi; asOf: string; onGiven: () => void }) {
  const w = useWords();
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(asOf);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const paise = typedPaise(amount);
    if (paise === undefined || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      setProblem(w.cashGiveOpeningInvalid);
      return;
    }
    setSaving(true);
    setProblem(undefined);
    api.giveBankOpening(paise, date).then(
      () => {
        setSaving(false);
        onGiven();
      },
      (error: unknown) => {
        setSaving(false);
        setProblem(w.cashGiveOpeningFailed(error instanceof ApiError ? error.problems : []));
      },
    );
  };
  return (
    <form className="cash-opening" aria-label={w.cashGiveOpening} onSubmit={submit}>
      <h3>{w.cashGiveOpening}</h3>
      <p className="muted">{w.cashGiveOpeningIntro}</p>
      <label>
        {w.cashGiveOpeningAmount}
        <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} required />
      </label>
      <label>
        {w.cashGiveOpeningDate}
        <input type="date" value={date} max={asOf} onChange={(e) => setDate(e.target.value)} required />
      </label>
      <button type="submit" disabled={saving}>
        {w.cashGiveOpeningSave}
      </button>
      {problem ? <p role="alert">{problem}</p> : null}
    </form>
  );
}

/** Plain left clicks stay in the app; others open as the browser does. */
const inApp = (event: MouseEvent) =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

/** A Document's entry: its Run in the app when it has one, an invoice's PDF otherwise, or its name alone. */
function DocumentLink({
  label,
  object,
  id,
  run,
  onOpenRun,
}: {
  label: string;
  object: string;
  id: string;
  run: string | null;
  onOpenRun: (run: string) => void;
}) {
  if (run !== null)
    return (
      <a
        className="linkish"
        href={`${PAGES.runs.path}/${run}`}
        onClick={(event) => {
          if (!inApp(event)) return;
          event.preventDefault();
          onOpenRun(run);
        }}
      >
        {label}
      </a>
    );
  if (object === "invoice")
    return (
      <a className="linkish" href={`/api/documents/${id}/pdf`} target="_blank" rel="noopener">
        {label}
      </a>
    );
  return <>{label}</>;
}

/** A part of the page LAMP does not show the person, and why. */
function NotShown({ part }: { part: Unshown }) {
  const w = useWords();
  return (
    <p className="muted">
      {part.status === "hidden" ? w.cashHidden(part.needs.map(w.sensitiveClass)) : w.cashCouldNotRead(part.problems)}
    </p>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section aria-labelledby={id}>
      <h3 className="sectionh" id={id}>
        {title}
      </h3>
      {children}
    </section>
  );
}

function AgeingTable({
  ageing,
  kind,
  onOpenRun,
}: {
  ageing: Ageing;
  kind: "receivables" | "payables";
  onOpenRun: (run: string) => void;
}) {
  const w = useWords();
  const { language } = useLanguage();
  if (ageing.status !== "shown") return <NotShown part={ageing} />;
  if (ageing.open.length === 0)
    return <p className="muted">{kind === "receivables" ? w.cashNothingOwedToYou : w.cashNothingYouOwe}</p>;
  return (
    <>
      <div className="tbl-wrap">
        <table className="tbl">
          <caption className="sr-only">{w.cashAgeing}</caption>
          <thead>
            <tr>
              {AGEING_BUCKETS.map((bucket) => (
                <th key={bucket} scope="col" className="r">
                  {w.cashBucket(bucket)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {AGEING_BUCKETS.map((bucket) => (
                <td key={bucket} className="r">
                  {rupees(ageing.buckets[bucket])}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th scope="col">{kind === "receivables" ? w.cashInvoice : w.cashBill}</th>
              <th scope="col">{kind === "receivables" ? w.cashCustomer : w.cashSupplier}</th>
              <th scope="col">{w.cashDue}</th>
              <th scope="col" className="r">
                {w.cashDaysLate}
              </th>
              <th scope="col" className="r">
                {kind === "receivables" ? w.cashOwedToYouColumn : w.cashYouOweColumn}
              </th>
            </tr>
          </thead>
          <tbody>
            {ageing.open.map((item) => (
              <tr key={item.id}>
                <th scope="row">
                  <DocumentLink
                    label={item.number}
                    object={kind === "receivables" ? "invoice" : "supplier_bill"}
                    id={item.id}
                    run={item.run}
                    onOpenRun={onOpenRun}
                  />
                </th>
                <td>{item.counterparty || "—"}</td>
                <td>{dayOf(item.dueDate, language)}</td>
                <td className="r">
                  {item.daysOverdue > 0 ? <span className="tag tag-bad">{item.daysOverdue}</span> : "—"}
                </td>
                <td className="r">{rupees(item.outstanding)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Forecast({ forecast }: { forecast: CashPage["forecast"] }) {
  const w = useWords();
  const { language } = useLanguage();
  if (forecast.status === "not_given")
    return (
      <p className="muted">
        {w.cashForecastNotGiven(forecast.since === null ? null : dayOf(forecast.since, language))}
      </p>
    );
  if (forecast.status !== "shown") return <NotShown part={forecast} />;
  const { weeks, negativeWeeks } = forecast.forecast;
  const [first] = negativeWeeks;
  return (
    <>
      <p className="muted">{w.cashForecastIntro}</p>
      {first !== undefined ? <p className="warn">{w.cashForecastNegative(first)}</p> : null}
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th scope="col">{w.cashWeek}</th>
              <th scope="col" className="r">
                {w.cashIn}
              </th>
              <th scope="col" className="r">
                {w.cashOut}
              </th>
              <th scope="col" className="r">
                {w.cashClosing}
              </th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((week) => (
              <tr key={week.week}>
                <th scope="row">{w.cashWeekSpan(dayOf(week.from, language), dayOf(week.to, language))}</th>
                <td className="r">{rupees(week.receipts)}</td>
                <td className="r">{rupees(week.payments)}</td>
                <td className="r">
                  {rupees(week.closing)}
                  {week.closing < 0 ? (
                    <>
                      {" "}
                      <span className="tag tag-bad">{w.cashBelowNil}</span>
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** A balance with the side it stands on: what a debit or credit balance means is the account's. */
function balanceWords(w: ReturnType<typeof useWords>, amount: number): string {
  return amount < 0 ? w.cashCreditBalance(rupees(-amount)) : w.cashDebitBalance(rupees(amount));
}

function Ledger({ account, onOpenRun }: { account: LedgerAccount; onOpenRun: (run: string) => void }) {
  const w = useWords();
  const { language } = useLanguage();
  const name = w.cashAccountName(account.account, account.name);
  return (
    <details className="card">
      <summary>
        <b>{name}</b> {balanceWords(w, account.balance)}
      </summary>
      <div className="tbl-wrap">
        <table className="tbl">
          <caption className="sr-only">{name}</caption>
          <thead>
            <tr>
              <th scope="col">{w.cashDate}</th>
              <th scope="col">{w.cashDocument}</th>
              <th scope="col">{w.cashCounterparty}</th>
              <th scope="col" className="r">
                {w.cashDebit}
              </th>
              <th scope="col" className="r">
                {w.cashCredit}
              </th>
            </tr>
          </thead>
          <tbody>
            {/* One posting may put two equal lines on one account: each row is keyed by its place too. */}
            {account.entries.map((entry, at) => (
              <tr key={`${entry.posting}:${String(at)}`}>
                <td>{dayOf(entry.date, language)}</td>
                <th scope="row">
                  <DocumentLink
                    label={w.cashDocumentName(entry.document.object, entry.document.number)}
                    object={entry.document.object}
                    id={entry.document.id}
                    run={entry.run}
                    onOpenRun={onOpenRun}
                  />
                </th>
                <td>{entry.counterparty ?? "—"}</td>
                <td className="r">{entry.amount > 0 ? rupees(entry.amount) : ""}</td>
                <td className="r">{entry.amount < 0 ? rupees(-entry.amount) : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {account.count > account.entries.length ? (
        <p className="small muted">{w.cashShowing(account.entries.length, account.count)}</p>
      ) : null}
    </details>
  );
}

/** The stock Ledger's balance: the stock on hand at weighted average purchase cost, its own line among the Ledgers. */
function Stock({ stock }: { stock: CashPage["stock"] }) {
  const w = useWords();
  if (stock.status !== "shown") return <NotShown part={stock} />;
  return (
    <p className="card" aria-label={w.cashStock}>
      <b>{w.cashStock}</b> {rupees(stock.stock.value)}
      <span className="small muted">
        {" "}
        {w.cashStockHow}
        {stock.stock.unvalued > 0 ? ` ${w.cashStockUnvalued(stock.stock.unvalued)}` : ""}
      </span>
    </p>
  );
}

/** What a business with nothing recorded yet would record for the page to fill. */
function NothingYet() {
  const w = useWords();
  return (
    <section className="card" aria-labelledby="cash-empty">
      <h3 id="cash-empty">{w.cashEmptyTitle}</h3>
      <p>{w.cashEmptyIntro}</p>
      <ul>
        <li>{w.cashEmptyInvoice}</li>
        <li>{w.cashEmptyBill}</li>
        <li>{w.cashEmptyPayment}</li>
      </ul>
    </section>
  );
}

export function Cash({ api, onOpenRun }: { api: CashApi; onOpenRun: (run: string) => void }) {
  const w = useWords();
  const { language } = useLanguage();
  const [page, setPage] = useState<CashPage | undefined>(undefined);
  const [problem, setProblem] = useState<"refused" | "failed" | undefined>(undefined);

  const [read, setRead] = useState(0);
  useEffect(() => {
    api
      .cash()
      .then(setPage, (error: unknown) =>
        setProblem(error instanceof ApiError && error.status === 403 ? "refused" : "failed"),
      );
  }, [api, read]);

  if (!page)
    return problem ? (
      <p role="alert">{problem === "refused" ? w.cashRefused : w.cashLoadFailed}</p>
    ) : (
      <p role="status">{w.cashLoading}</p>
    );
  const amountOf = (account: string) => page.balances.find((b) => b.account === account)?.amount ?? 0;
  const total = (ageing: Ageing) => (ageing.status === "shown" ? rupees(ageing.total) : "—");
  /** Where the bank's balance starts: its opening balance and where it came from, or since when it is not given. */
  const bankNote = (opening: BankOpening, posted: number) =>
    opening.status === "given"
      ? w.cashBankFrom(
          rupees(opening.amount),
          opening.asOf === null ? null : dayOf(opening.asOf, language),
          opening.source === "tally",
        )
      : w.cashBankNotGiven(rupees(posted), opening.since === null ? null : dayOf(opening.since, language));
  return (
    <>
      <div className="hello">
        <h2>{w.cashHeading}</h2>
        <p className="muted">{w.cashIntro(dayOf(page.asOf, language))}</p>
      </div>
      {page.empty ? (
        <NothingYet />
      ) : (
        <>
          <div className="kpis">
            {[
              {
                label: w.cashBank,
                value: page.bankOpening.status === "given" ? rupees(amountOf("bank")) : w.cashBankUnknown,
                note: bankNote(page.bankOpening, amountOf("bank")),
              },
              { label: w.cashInHand, value: rupees(amountOf("cash")) },
              { label: w.cashOwedToYou, value: total(page.receivables) },
              { label: w.cashYouOwe, value: total(page.payables) },
            ].map((tile) => (
              <article className="kpi" key={tile.label}>
                <h3>{tile.label}</h3>
                <p className="kpi-now">
                  <b>{tile.value}</b>
                </p>
                {"note" in tile ? <p className="kpi-reason">{tile.note}</p> : null}
              </article>
            ))}
          </div>
          {page.bankOpening.status === "not_given" && page.bankOpening.mayGive ? (
            <GiveOpening api={api} asOf={page.asOf} onGiven={() => setRead((n) => n + 1)} />
          ) : null}
          <Section id="cash-receivables" title={w.cashReceivables}>
            <AgeingTable ageing={page.receivables} kind="receivables" onOpenRun={onOpenRun} />
          </Section>
          <Section id="cash-payables" title={w.cashPayables}>
            <AgeingTable ageing={page.payables} kind="payables" onOpenRun={onOpenRun} />
          </Section>
          <Section id="cash-forecast" title={w.cashForecastTitle}>
            <Forecast forecast={page.forecast} />
          </Section>
          <Section id="cash-ledgers" title={w.cashLedgersTitle}>
            <Stock stock={page.stock} />
            {page.ledgers.status === "shown" ? (
              <>
                <p className="muted">{w.cashLedgersIntro}</p>
                {page.ledgers.accounts.map((account) => (
                  <Ledger key={account.account} account={account} onOpenRun={onOpenRun} />
                ))}
              </>
            ) : (
              <NotShown part={page.ledgers} />
            )}
          </Section>
        </>
      )}
    </>
  );
}
